import React, { useState, useEffect } from 'react';

import { User, Calendar, FileText, Mail } from 'lucide-react';
import jsPDF from 'jspdf';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DashboardWidget from './components/DashboardWidget';
import PayslipCard from './components/PayslipCard';
import LeaveBalanceCard from './components/LeaveBalanceCard';
import LeaveRequestForm from './components/LeaveRequestForm';
import LeaveRequestsList from './components/LeaveRequestsList';
import PersonalRecordsForm from './components/PersonalRecordsForm';
import LetterRequestForm from './components/LetterRequestForm';
import LetterCard from './components/LetterCard';
import LetterDetailsModal from './components/LetterDetailsModal';
import employeeSelfServiceService from '../../services/employeeSelfServiceService';
import letterRequestService from '../../services/letterRequestService';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';



const EmployeeSelfServicePortal = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const { companySettings } = useCompanySettings();

  const companyName = companySettings?.companyName || 'HR Analytics';

  const [showLeaveRequestForm, setShowLeaveRequestForm] = useState(false);
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  // Letter management state
  const [letterRequests, setLetterRequests] = useState([]);
  const [showLetterRequestForm, setShowLetterRequestForm] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showLetterDetailsModal, setShowLetterDetailsModal] = useState(false);
  const [generatingLetter, setGeneratingLetter] = useState(false);

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    if (user?.email) {
      loadEmployeeData();
      checkSubscription();
    }
  }, [user]);

  const loadEmployeeData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get employee by email from authenticated user
      const employeeId = await employeeSelfServiceService?.getEmployeeIdByEmail(user?.email);
      
      if (!employeeId) {
        setError('Employee profile not found. Please contact HR.');
        setLoading(false);
        return;
      }
      
      const [profile, slips, balances, requests, letters] = await Promise.all([
        employeeSelfServiceService?.getEmployeeProfile(employeeId)?.catch(() => null),
        employeeSelfServiceService?.getEmployeePayslips(employeeId)?.catch(() => []),
        employeeSelfServiceService?.getLeaveBalances(employeeId)?.catch(() => []),
        employeeSelfServiceService?.getLeaveRequests(employeeId)?.catch(() => []),
        letterRequestService?.getEmployeeLetterRequests(employeeId)?.catch(() => [])
      ]);

      setEmployeeProfile(profile);
      setPayslips(slips);
      setLeaveBalances(balances);
      setLeaveRequests(requests);
      setLetterRequests(letters);
    } catch (err) {
      setError(err?.message || 'Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleDownloadPayslip = (payslip) => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc?.setFontSize(18);
      doc?.text(`${companyName} - Payslip`, 20, 20);
      
      // Employee info
      doc?.setFontSize(12);
      doc?.text(`Employee: ${employeeProfile?.firstName} ${employeeProfile?.lastName}`, 20, 40);
      doc?.text(`Employee Number: ${employeeProfile?.employeeNumber}`, 20, 50);
      doc?.text(`Department: ${employeeProfile?.department}`, 20, 60);
      doc?.text(`Payslip Number: ${payslip?.payslipNumber}`, 20, 70);
      
      // Pay period
      doc?.text(`Pay Period: ${new Date(payslip?.payPeriodStart)?.toLocaleDateString()} - ${new Date(payslip?.payPeriodEnd)?.toLocaleDateString()}`, 20, 80);
      doc?.text(`Payment Date: ${new Date(payslip?.paymentDate)?.toLocaleDateString()}`, 20, 90);
      
      // Earnings and deductions
      if (payslip?.details) {
        doc?.text('Earnings:', 20, 110);
        doc?.text(`Base Pay: K${payslip?.details?.basePay?.toLocaleString()}`, 30, 120);
        doc?.text(`Overtime: K${payslip?.details?.overtimePay?.toLocaleString()}`, 30, 130);
        doc?.text(`Allowances: K${payslip?.details?.allowances?.toLocaleString()}`, 30, 140);
        doc?.text(`Gross Pay: K${payslip?.details?.grossPay?.toLocaleString()}`, 30, 150);
        
        doc?.text('Deductions:', 20, 170);
        doc?.text(`SWT: K${payslip?.details?.swtAmount?.toLocaleString()}`, 30, 180);
        doc?.text(`NASFUND: K${payslip?.details?.employeeSuper?.toLocaleString()}`, 30, 190);
        doc?.text(`Other Deductions: K${payslip?.details?.deductions?.toLocaleString()}`, 30, 200);
        
        doc?.setFontSize(14);
        doc?.text(`Net Pay: K${payslip?.details?.netPay?.toLocaleString()}`, 20, 220);
      }
      
      doc?.save(`payslip-${payslip?.payslipNumber}.pdf`);
      setSuccess('Payslip downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate payslip PDF');
    }
  };

  const handleSubmitLeaveRequest = async (requestData) => {
    try {
      setSubmittingLeave(true);
      setError('');
      
      const employeeId = await employeeSelfServiceService?.getEmployeeIdByEmail(user?.email);
      await employeeSelfServiceService?.submitLeaveRequest(employeeId, requestData);
      
      setSuccess('Leave request submitted successfully');
      setShowLeaveRequestForm(false);
      loadEmployeeData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to submit leave request');
    } finally {
      setSubmittingLeave(false);
    }
  };

  const handleCancelLeaveRequest = async (requestId) => {
    if (!confirm('Are you sure you want to cancel this leave request?')) return;
    
    try {
      setError('');
      await employeeSelfServiceService?.cancelLeaveRequest(requestId);
      setSuccess('Leave request cancelled successfully');
      loadEmployeeData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to cancel leave request');
    }
  };

  const handleSavePersonalRecords = async (updates) => {
    try {
      setSavingProfile(true);
      setError('');
      
      const employeeId = await employeeSelfServiceService?.getEmployeeIdByEmail(user?.email);
      await employeeSelfServiceService?.updatePersonalRecords(employeeId, updates);
      
      setSuccess('Personal records updated successfully. Changes pending HR approval.');
      loadEmployeeData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err?.message || 'Failed to update personal records');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCreateLetterRequest = async (requestData) => {
    try {
      setGeneratingLetter(true);
      setError('');
      
      const employeeId = await employeeSelfServiceService?.getEmployeeIdByEmail(user?.email);
      const letterRequest = await letterRequestService?.createLetterRequest(
        employeeId,
        requestData?.letterPurpose,
        requestData?.additionalDetails
      );
      
      // Submit for approval immediately
      await letterRequestService?.submitLetterForApproval(letterRequest?.id, employeeId);
      
      setSuccess('Letter generated and submitted for HR approval!');
      setShowLetterRequestForm(false);
      loadEmployeeData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to generate letter');
    } finally {
      setGeneratingLetter(false);
    }
  };

  const handleDownloadLetter = async (letter) => {
    try {
      const doc = new jsPDF();
      
      // Parse letter content and format for PDF
      const lines = letter?.generatedContent?.split('\n') || [];
      let yPosition = 20;
      
      doc?.setFontSize(11);
      lines?.forEach((line) => {
        if (yPosition > 280) {
          doc?.addPage();
          yPosition = 20;
        }
        doc?.text(line, 20, yPosition);
        yPosition += 7;
      });
      
      // Add signature block if approved
      if (letter?.hrSignatureName) {
        yPosition += 10;
        doc?.setFontSize(10);
        doc?.text(`Signed by: ${letter?.hrSignatureName}`, 20, yPosition);
        yPosition += 7;
        doc?.text(`Title: ${letter?.hrSignatureTitle}`, 20, yPosition);
        yPosition += 7;
        doc?.text(`Date: ${new Date(letter?.hrSignatureDate)?.toLocaleDateString()}`, 20, yPosition);
      }
      
      const purposeLabels = {
        banking: 'Banking',
        superannuation_withdrawal: 'Superannuation',
        staff_reference: 'Reference',
      };
      
      const filename = `${purposeLabels?.[letter?.letterPurpose]}_Letter_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
      doc?.save(filename);
      
      // Track download
      await letterRequestService?.trackLetterDownload(letter?.id);
      
      setSuccess('Letter downloaded successfully');
      loadEmployeeData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading letter:', err);
      setError('Failed to download letter');
    }
  };

  const formatCurrency = (amount) => {
    return `K${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'payslips', label: 'Payslips', icon: 'FileText' },
    { id: 'letters', label: 'Letters', icon: 'Mail' },
    { id: 'leave', label: 'Leave Management', icon: 'Calendar' },
    { id: 'profile', label: 'Personal Records', icon: 'User' }
  ];

  const recentPayslip = payslips?.[0];
  const totalLeaveBalance = leaveBalances?.reduce((sum, balance) => sum + (balance?.balanceDays || 0), 0);
  const pendingRequests = leaveRequests?.filter(req => req?.leaveStatus === 'pending')?.length || 0;
  const pendingLetters = letterRequests?.filter(req => req?.letterStatus === 'pending_approval')?.length || 0;
  const approvedLetters = letterRequests?.filter(req => req?.letterStatus === 'approved' || req?.letterStatus === 'downloaded')?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <LegalDisclaimerBanner />
      <GlobalFilterBar onFilterChange={() => {}} />
      <DataRefreshIndicator />

      <main className="pt-32 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="User" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  Welcome, {employeeProfile?.firstName || 'Employee'}
                </h1>
                <p className="text-muted-foreground">
                  {employeeProfile?.position} • {employeeProfile?.department}
                </p>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 bg-success/10 border border-success/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-success text-sm">{success}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6 border-b border-border">
            <div className="flex gap-1 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-medium transition-smooth whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Icon name="Loader2" className="animate-spin h-12 w-12 text-primary" />
            </div>
          )}

          {/* Dashboard Tab */}
          {!loading && activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Dashboard Widgets */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <DashboardWidget
                  title="Recent Payslip"
                  value={recentPayslip ? formatCurrency(recentPayslip?.details?.netPay) : 'N/A'}
                  subtitle={recentPayslip ? `Pay Period: ${new Date(recentPayslip?.payPeriodEnd)?.toLocaleDateString()}` : 'No payslips available'}
                  icon="DollarSign"
                  iconColor="text-success"
                  onClick={() => setActiveTab('payslips')}
                  action=""
                  onActionClick={() => {}}
                />
                <DashboardWidget
                  title="Leave Balance"
                  value={`${totalLeaveBalance} days`}
                  subtitle={`${pendingRequests} pending request${pendingRequests !== 1 ? 's' : ''}`}
                  icon="Calendar"
                  iconColor="text-primary"
                  onClick={() => setActiveTab('leave')}
                  action=""
                  onActionClick={() => {}}
                />
                <DashboardWidget
                  title="Letters"
                  value={`${approvedLetters} approved`}
                  subtitle={`${pendingLetters} pending approval`}
                  icon="Mail"
                  iconColor="text-purple-600"
                  onClick={() => setActiveTab('letters')}
                  action=""
                  onActionClick={() => {}}
                />
                <DashboardWidget
                  title="Profile Status"
                  value="Active"
                  subtitle={`Since ${employeeProfile?.hireDate ? new Date(employeeProfile?.hireDate)?.toLocaleDateString() : 'N/A'}`}
                  icon="User"
                  iconColor="text-warning"
                  onClick={() => setActiveTab('profile')}
                  action=""
                  onActionClick={() => {}}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-4">Recent Payslip</h3>
                  {recentPayslip ? (
                    <PayslipCard payslip={recentPayslip} onDownload={handleDownloadPayslip} />
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No payslips available</p>
                  )}
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-4">Leave Balances</h3>
                  <div className="space-y-3">
                    {leaveBalances?.length > 0 ? (
                      leaveBalances?.map(balance => (
                        <LeaveBalanceCard 
                          key={balance?.id} 
                          balance={balance}
                          leaveType={balance?.leaveType || 'N/A'}
                          accruedDays={balance?.accruedDays || 0}
                          usedDays={balance?.usedDays || 0}
                          balanceDays={balance?.balanceDays || 0}
                        />
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No leave balances available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payslips Tab */}
          {!loading && activeTab === 'payslips' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-heading font-semibold text-foreground">Payslip History</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {payslips?.length > 0 ? (
                  payslips?.map(payslip => (
                    <PayslipCard key={payslip?.id} payslip={payslip} onDownload={handleDownloadPayslip} />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No payslips available</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Letters Tab */}
          {!loading && activeTab === 'letters' && (
            <div className="space-y-6">
              {/* Header with Action Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">My Letters</h2>
                  <p className="text-muted-foreground mt-1">
                    Request and download employment letters
                  </p>
                </div>
                {!showLetterRequestForm && (
                  <Button
                    variant="default"
                    iconName="Plus"
                    onClick={() => {
                      if (isEditable('letters')) {
                        setShowLetterRequestForm(true);
                      } else {
                        startEdit('letters');
                      }
                    }}
                  >
                    Request New Letter
                  </Button>
                )}
              </div>

              {/* Letter Request Form */}
              {showLetterRequestForm && (
                <LetterRequestForm
                  onSubmit={handleCreateLetterRequest}
                  onCancel={() => setShowLetterRequestForm(false)}
                  loading={generatingLetter}
                />
              )}

              {/* Letters List */}
              {!showLetterRequestForm && (
                <div>
                  {letterRequests?.length === 0 ? (
                    <div className="bg-card border border-border rounded-lg p-12 text-center">
                      <Icon name="Mail" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Letters Yet</h3>
                      <p className="text-muted-foreground mb-6">
                        Request employment letters for banking, superannuation, or reference purposes.
                      </p>
                      <Button
                        variant="default"
                        iconName="Plus"
                        onClick={() => {
                          if (isEditable('letters')) {
                            setShowLetterRequestForm(true);
                          } else {
                            startEdit('letters');
                          }
                        }}
                      >
                        Request Your First Letter
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {letterRequests?.map((letter) => (
                        <LetterCard
                          key={letter?.id}
                          letter={letter}
                          onDownload={handleDownloadLetter}
                          onViewDetails={(letter) => {
                            setSelectedLetter(letter);
                            setShowLetterDetailsModal(true);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Leave Tab */}
          {!loading && activeTab === 'leave' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-heading font-semibold text-foreground">Leave Management</h2>
                {!showLeaveRequestForm && (
                  <Button
                    variant="primary"
                    iconName="Plus"
                    onClick={() => setShowLeaveRequestForm(true)}
                  >
                    Request Leave
                  </Button>
                )}
              </div>

              {showLeaveRequestForm && (
                <div className="bg-card border border-border rounded-lg p-6 mb-6">
                  <h3 className="font-heading font-semibold text-foreground mb-4">New Leave Request</h3>
                  <LeaveRequestForm
                    leaveBalances={leaveBalances}
                    onSubmit={handleSubmitLeaveRequest}
                    onCancel={() => setShowLeaveRequestForm(false)}
                    loading={submittingLeave}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {leaveBalances?.map(balance => (
                  <LeaveBalanceCard 
                    key={balance?.id} 
                    balance={balance}
                    leaveType={balance?.leaveType || 'N/A'}
                    accruedDays={balance?.accruedDays || 0}
                    usedDays={balance?.usedDays || 0}
                    balanceDays={balance?.balanceDays || 0}
                  />
                ))}
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-heading font-semibold text-foreground mb-4">Leave Request History</h3>
                <LeaveRequestsList
                  requests={leaveRequests}
                  onCancel={handleCancelLeaveRequest}
                  loading={false}
                />
              </div>
            </div>
          )}

          {/* Profile Tab */}
          {!loading && activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-6">Personal Records</h2>
                <PersonalRecordsForm
                  profile={employeeProfile}
                  employee={employeeProfile}
                  onSave={handleSavePersonalRecords}
                  loading={savingProfile}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Letter Details Modal */}
      {showLetterDetailsModal && selectedLetter && (
        <LetterDetailsModal
          letter={selectedLetter}
          onClose={() => {
            setShowLetterDetailsModal(false);
            setSelectedLetter(null);
          }}
          onDownload={handleDownloadLetter}
        />
      )}

      {/* Teaser Edit Modal */}
      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default EmployeeSelfServicePortal;